import { FC, PropsWithChildren, ReactNode } from 'react'

import * as DialogPrimitive from '@radix-ui/react-dialog'
import { styled } from 'styled-components'

import Icon from 'core/components/Icon'
import HelpText from 'core/components/lib/HelpText'
import { onSurface, onSurfaceVariant, variables } from 'core/styles'

type DialogProps = {
  padding?: string
  width?: 'mini' | 'small' | 'medium'
  heightContainer?: 'mini' | 'small' | 'medium' | 'large'
  title?: ReactNode
  subtitle?: ReactNode
  helpText?: ReactNode
  content: ReactNode
  isOpen?: boolean
  onOpenChange?: (isOpen: boolean) => void
  variant?: 'default' | 'compact' | 'mini'
}

const Dialog: FC<PropsWithChildren<DialogProps>> = ({
  children,
  padding = '24px',
  width = 'small',
  heightContainer,
  title,
  subtitle,
  helpText,
  content,
  isOpen,
  onOpenChange,
  variant = 'default',
}) => (
  <DialogPrimitive.Root onOpenChange={onOpenChange} open={isOpen}>
    <DialogPrimitive.Trigger asChild>{children}</DialogPrimitive.Trigger>
    <DialogPrimitive.Portal>
      <Overlay>
        <Content
          $maxHeight={
            heightContainer === 'small' ? '40vh'
            : heightContainer === 'medium' ?
              '65vh'
            : heightContainer === 'large' ?
              '90vh'
            : undefined
          }
          $overflowY={heightContainer ? 'auto' : undefined}
          $padding={padding}
          $variant={variant}
          $width={
            width === 'mini' ? '360px'
            : width === 'small' ?
              '480px'
            : '680px'
          }
        >
          {variant !== 'mini' && variant !== 'compact' && (
            <CloseButton>
              <Icon color={variables.colorWhite} fontSize='28px' name='clear' />
            </CloseButton>
          )}
          {(!!title || !!subtitle) && (
            <Header $variant={variant}>
              <HelpText helpTextMargin='16px 24px 0' iconMargin='0 16px 0 0' text={helpText}>
                <Title $variant={variant} margin={padding}>
                  {title}
                </Title>
              </HelpText>
              {!!subtitle && <Subtitle>{subtitle}</Subtitle>}
            </Header>
          )}
          <Body $variant={variant} margin={padding}>
            {content}
          </Body>
        </Content>
      </Overlay>
    </DialogPrimitive.Portal>
  </DialogPrimitive.Root>
)

export default Dialog

const Overlay = styled(DialogPrimitive.Overlay)`
  display: grid;
  position: fixed;
  inset: 0;
  place-items: center;
  z-index: 10;
  background: ${variables.overlayDark};
  padding: 64px 8px;
  overflow-y: auto;
`

const CloseButton = styled(DialogPrimitive.Close)`
  position: absolute;
  top: 0;
  right: 0;
  transform: translate(32px, -32px);
  outline-offset: 2px;
  border: none;
  border-radius: 28px;
  background: none;
  cursor: pointer;
`

const Content = styled(DialogPrimitive.Content)<{
  $width: string
  $maxHeight?: string
  $padding: string
  $overflowY?: string
  $variant: 'default' | 'compact' | 'mini'
}>`
  box-sizing: border-box;
  position: relative;
  border-radius: 8px;
  background: ${variables.colorWhite};
  padding: ${(p) => (p.$variant === 'default' ? p.$padding : '16px')};
  padding-right: 0;
  padding-bottom: 0;
  padding-left: 0;
  width: 100%;
  max-width: ${(p) => p.$width};
  ${({ $maxHeight }) => ($maxHeight ? `max-height: ${$maxHeight};` : '')}
  ${({ $overflowY }) => ($overflowY ? ` overflow-y: ${$overflowY};` : '')}
`

const Body = styled.div<{ margin: string; $variant: 'default' | 'compact' | 'mini' }>`
  margin: ${(p) => (p.$variant === 'default' ? p.margin : '16px')};
  margin-top: 0;
  ${(p) => (p.$variant === 'mini' ? 'font-size: 14px;' : '')}
`

const Header = styled.header<{ $variant: 'default' | 'compact' | 'mini' }>`
  margin-bottom: ${(p) =>
    p.$variant === 'mini' ? '16px'
    : p.$variant === 'compact' ? '32px'
    : '16px'};
`

const Title = styled(DialogPrimitive.Title)<{ margin: string; $variant: 'default' | 'compact' | 'mini' }>`
  margin: ${(p) => (p.$variant === 'default' ? p.margin : '16px')};
  margin-top: 0;
  margin-bottom: 0;
  color: ${onSurface};
  font-size: ${(p) =>
    p.$variant === 'mini' ? '19px'
    : p.$variant === 'compact' ? '20px'
    : '2.2rem'};
  font-weight: ${(p) => (p.$variant === 'default' ? 400 : 600)};
`

const Subtitle = styled.p`
  margin: 0 0 16px 24px;
  color: ${onSurfaceVariant};
  font-size: 16px;
`

export const DialogText = styled.p`
  margin: 0;
  color: ${variables.colorBlack60};

  & + & {
    margin-top: 8px;
  }
`

export const DialogActions = styled.div<{ $variant?: 'default' | 'compact' | 'mini' }>`
  display: flex;
  gap: ${(p) => (p.$variant === 'default' ? '32px' : '16px')};
  align-items: center;
  justify-content: ${(p) => (p.$variant === 'mini' ? 'flex-start' : 'flex-end')};
  margin: ${(p) => (p.$variant === 'compact' ? '16px 0 0' : '32px 0 0')};
  width: 100%;
`
